import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowUpRightIcon, TriangleAlertIcon } from 'lucide-react';
import { trackEvent } from '../../utils/analytics';
import { ATNA_CONFIG, VerticalConfig } from './atnaConfig';
import { useLanguage } from '../LanguageContext';
import { findFaqQuestion, resolveAnswer, questionLabel, VerificationScore, VerificationLabel, WhyThis, InformActNote } from './verification';
export function AtnaLauncher() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [typed, setTyped] = useState('');
  const [searchMessage, setSearchMessage] = useState<string | null>(null);
  const location = useLocation();
  let verticalTag = 'home';
  if (location.pathname.includes('brossard')) verticalTag = 'brossard';
  const vertical: VerticalConfig = ATNA_CONFIG[verticalTag] || ATNA_CONFIG.other;
  useEffect(() => {
    if (location.pathname.includes('request-demo')) {
      setIsVisible(false);
      setIsOpen(false);
      return;
    }
    const handleScroll = () => setIsVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
  useEffect(() => {
    const handleExternalOpen = () => {
      setIsOpen(true);
      trackEvent('atna_opened', {
        page: location.pathname,
        vertical: vertical.tag,
        location: 'external_trigger'
      });
    };
    window.addEventListener('atna:open', handleExternalOpen);
    return () => window.removeEventListener('atna:open', handleExternalOpen);
  }, [location.pathname, vertical.tag]);
  const pick = (question: string) => {
    setActiveQuestion(question);
    setExpanded(false);
    setSearchMessage(null);
    trackEvent('atna_question_clicked', {
      question,
      page: location.pathname,
      vertical: vertical.tag
    });
  };
  const submitSearch = () => {
    const matchingQuestion = findFaqQuestion(vertical.questions, typed);
    if (matchingQuestion) {
      pick(matchingQuestion);
      return;
    }
    setSearchMessage(isFR ? 'Aucune question correspondante. Essayez par exemple « intégration », « déploiement », « vérification » ou « démo ».' : 'No matching FAQ found. Try “integration,” “deployment,” “verification,” or “demo.”');
  };
  const handleOpen = () => {
    setIsOpen(true);
    trackEvent('atna_opened', {
      page: location.pathname,
      vertical: vertical.tag
    });
  };
  const handleClose = () => {
    setIsOpen(false);
    setActiveQuestion(null);
    setExpanded(false);
    setTyped('');
    setSearchMessage(null);
  };
  const reset = () => {
    setActiveQuestion(null);
    setExpanded(false);
    setTyped('');
    setSearchMessage(null);
  };
  const resolved = resolveAnswer(vertical, activeQuestion, isFR);
  return <>
      <AnimatePresence data-id="element-134">
        {isVisible && !isOpen && <motion.button initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} onClick={handleOpen} className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-[#121212] text-white px-5 py-4 rounded-none shadow-lg hover:bg-black transition-colors" data-id="element-135">
            <span className="w-3 h-3 bg-[#4F46E5] -skew-x-[30deg]" aria-hidden="true" data-id="element-136" />
            <span className="font-sans text-sm font-medium tracking-wide" data-id="element-137">
              {isFR ? 'Demander à ATNA' : 'Ask ATNA'}
            </span>
          </motion.button>}
      </AnimatePresence>

      <AnimatePresence data-id="element-138">
        {isOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed top-0 right-0 bottom-0 w-full md:w-[440px] bg-white z-50 shadow-2xl border-l border-[#E4E6EC] flex flex-col" data-id="element-139">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-140">
              <div className="font-sans text-xs font-semibold tracking-[0.15em] text-[#121212]/70 uppercase" data-id="element-141">
                {isFR ? 'ATNA Assistant · Propulsé par ATNA' : 'ATNA Assistant · Powered by ATNA'}
              </div>
              <button onClick={handleClose} className="text-[#121212]/40 hover:text-[#121212] transition-colors" aria-label={isFR ? 'Fermer ATNA' : 'Close ATNA'} data-id="element-142">
                <XIcon className="w-5 h-5" data-id="element-143" />
              </button>
            </div>

            <div className="px-5 py-2 border-b border-[#E4E6EC] bg-[#FAFAFB] font-sans text-[11px] text-[#5B6478]" data-id="element-144">
              {isFR ? 'Démonstration · valeurs illustratives' : 'Demonstration · illustrative values'}
            </div>

            <div className="flex-1 overflow-y-auto p-5" data-id="element-145">
              {!activeQuestion ? <div data-id="element-146">
                  <h3 className="font-serif text-xl text-[#121212] leading-snug mb-1" data-id="element-147">
                    {isFR ? 'Une intelligence vérifiée pour les collectivités.' : 'Verified intelligence for communities.'}
                  </h3>
                  <p className="font-sans text-sm text-[#5B6478] mb-6" data-id="element-148">
                    {isFR ? 'Explorez les questions fréquentes sur BCITI et ATNA.' : 'Explore frequently asked questions about BCITI and ATNA.'}
                  </p>
                  <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-3" data-id="element-149">
                    {isFR ? 'Questions fréquentes' : 'Frequently asked questions'}
                  </div>
                  <div className="flex flex-col gap-2.5 mb-6" data-id="element-150">
                    {vertical.questions.map(question => <button key={question} onClick={() => pick(question)} className="text-left border border-[#E4E6EC] bg-white rounded-lg px-4 py-3 font-sans text-sm text-[#121212] hover:border-[#4F46E5] transition-colors" data-id="element-151">
                        {questionLabel(question, isFR)}
                      </button>)}
                  </div>

                  <div className="flex items-center gap-2 border border-[#E4E6EC] px-3 py-2.5" data-id="element-152">
                    <input value={typed} onChange={event => {
                setTyped(event.target.value);
                setSearchMessage(null);
              }} onKeyDown={event => {
                if (event.key === 'Enter') submitSearch();
              }} placeholder={isFR ? 'Rechercher dans les questions fréquentes…' : 'Search the FAQ…'} className="flex-1 bg-transparent outline-none font-sans text-sm text-[#121212] placeholder:text-[#5B6478]" data-id="element-153" />
                    <button onClick={submitSearch} aria-label={isFR ? 'Rechercher' : 'Search'} className="shrink-0 w-8 h-8 bg-[#4F46E5] text-white flex items-center justify-center hover:bg-[#4F46E5]/90 transition-colors" data-id="element-154">
                      <ArrowUpRightIcon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-155" />
                    </button>
                  </div>
                  {searchMessage && <p role="status" className="mt-2 font-sans text-[11px] text-[#5B6478]" data-id="element-156">
                      {searchMessage}
                    </p>}
                </div> : resolved && <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} data-id="element-157">
                    <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-2" data-id="element-158">
                      {isFR ? 'Votre question' : 'You asked'}
                    </div>
                    <p className="font-serif text-base text-[#121212] mb-6" data-id="element-159">
                      {questionLabel(activeQuestion, isFR)}
                    </p>

                    <div className="font-sans text-xs font-semibold tracking-wider text-[#4F46E5] uppercase mb-2" data-id="element-160">
                      {isFR ? 'Réponse' : 'Answer'}
                    </div>
                    <p className="font-serif text-lg text-[#121212] leading-snug mb-6" data-id="element-161">
                      {resolved.recommendation}
                    </p>

                    {resolved.caution && <div role="note" className="flex items-start gap-3 border border-amber-300 bg-amber-50 px-4 py-3 mb-6" data-id="element-162">
                        <TriangleAlertIcon className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" data-id="element-163" />
                        <p className="font-sans text-sm leading-relaxed text-amber-950" data-id="element-164">
                          {resolved.caution}
                        </p>
                      </div>}

                    <div className="font-sans text-xs font-semibold tracking-wider text-[#5B6478] uppercase mb-3" data-id="element-165">
                      {isFR ? 'Points clés' : 'Key points'}
                    </div>
                    <div className="space-y-2 mb-6" data-id="element-166">
                      {resolved.results.slice(0, 4).map(result => <div key={result} className="flex items-start gap-2.5" data-id="element-167">
                          <span className="w-1.5 h-1.5 bg-[#4F46E5] shrink-0 mt-2" aria-hidden="true" data-id="element-168" />
                          <span className="font-sans text-sm text-[#121212]" data-id="element-169">
                            {result}
                          </span>
                        </div>)}
                    </div>

                    <VerificationScore label={resolved.label} isFR={isFR} expanded={expanded} onToggle={() => {
              setExpanded(value => !value);
              if (!expanded) {
                trackEvent('atna_verification_expanded', {
                  page: location.pathname,
                  vertical: vertical.tag,
                  location: 'launcher'
                });
              }
            }} data-id="element-170" />
                    <AnimatePresence initial={false} data-id="element-171">
                      {expanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.35
              }} className="overflow-hidden" data-id="element-172">
                          <div className="pt-4 space-y-4" data-id="element-173">
                            <VerificationLabel label={resolved.label} isFR={isFR} compact data-id="element-174" />
                            <WhyThis label={resolved.label} isFR={isFR} data-id="element-175" />
                            <InformActNote isFR={isFR} data-id="element-176" />
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    <div className="pt-6 mt-6 border-t border-[#E4E6EC]" data-id="element-177">
                      <button onClick={reset} className="font-sans text-sm font-medium text-[#4F46E5] hover:text-[#4F46E5]/80 transition-colors" data-id="element-178">
                        {isFR ? 'Poser une autre question' : 'Ask another question'}
                      </button>
                    </div>
                  </motion.div>}
            </div>

            <div className="p-5 border-t border-[#E4E6EC] bg-white" data-id="element-179">
              <Link to={`/request-demo?source=atna&vertical=${vertical.tag}`} onClick={() => {
            trackEvent('atna_demo_clicked', {
              page: location.pathname,
              vertical: vertical.tag,
              location: 'launcher'
            });
            setIsOpen(false);
          }} className="flex items-center justify-center gap-2 w-full py-3 bg-[#4F46E5] text-white text-sm font-medium hover:bg-[#4F46E5]/90 transition-colors" data-id="element-180">
                {isFR ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-181" />
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}